<template>
    <div v-show="!hasRecording" class="recording">
        <div class="titleContainer">
            <span class="menu-icon">
                <i class="bi bi-mic"></i>
            </span>
            <span>{{ $t('Audio Recording') }}</span>
        </div>
        <div class="recordContainer">
            <button class="btn p-0 circularButton closeButton" @click="cancelRecording">
                <i class="bi bi-x fs-1 p-0"></i>
            </button>
            <span class="recordProgress">{{ formattedRecordingProgress }}</span>
            <div id="mic"></div>
            <button class="btn p-0 circularButton" @click="stopRecording">
                <i class="bi bi-check2 text-white fs-2 p-0"></i>
            </button>
        </div>
    </div>
    <div v-show="hasRecording" class="preview">
        <div class="recordPlayBack">
            <button class="btn p-0 circularButton closeButton" @click="cancelRecording">
                <i class="bi bi-x fs-1 p-0"></i>
            </button>
            <span class="recordProgress">{{ formattedPlaybackTime }}</span>
            <div id="playback"></div>
            <button class="btn p-0 circularButton" @click="togglePlayback">
                <i
                    :class="['bi', { 'bi-play-fill': !playbackState.isPlaying }, { 'bi-pause-fill': playbackState.isPlaying }, , 'fs-2', 'text-white', 'p-0']"></i>
            </button>
        </div>
        <div class="d-grid justify-content-end">
            <button class="btn btn-primary width-fit-content" @click="uploadAudioToFirebase"
                :disabled="uploadAudioLoading">
                <div v-if="uploadAudioLoading">
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {{ $t("Sending") }}
                </div>
                <span v-else>{{ $t("Send") }}</span>
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js';
import { useStore } from 'vuex';
import { MessageTypes } from '@/components/whatsapp-managment/interfaces.js'
import { storage } from "@/firebase";
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage"
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import throttle from 'lodash/throttle';

const props = defineProps({
    conversation: Object,
});

const store = useStore();
const recordingWavesurfer = ref(null);
const playbackWavesurfer = ref(null);
const record = ref(null);
const progressTime = ref(0);
const hasRecording = ref(false);
const playbackState = ref({ isPlaying: false, currentTime: 0 });
const recordingState = ref('')
const uploadAudioLoading = ref(false)
const audioBlob = ref(null)

const convertWebMToOggOpus = async (webMBlob) => {
    const ffmpeg = new FFmpeg({ log: true });
    await ffmpeg.load();

    // Prepare the filenames for input and output
    const inputFileName = 'input.webm';
    const outputFileName = 'output.ogg';

    // Write the input WebM blob to FFmpeg's virtual file system
    await ffmpeg.writeFile(inputFileName, await fetchFile(webMBlob));

    // Execute the conversion command using the Opus codec
    await ffmpeg.exec(['-i', inputFileName, '-c:a', 'libopus', outputFileName]);

    // Read the converted OGG file from FFmpeg's file system
    const data = await ffmpeg.readFile(outputFileName);

    // Convert the Uint8Array to a Blob with the correct MIME type
    const oggBlob = new Blob([data.buffer], { type: 'audio/ogg; codecs=opus' });

    return oggBlob;
};

const formattedRecordingProgress = computed(() => {
    let totalSeconds = Math.floor(progressTime.value / 1000);
    let minutes = Math.floor(totalSeconds / 60);
    let seconds = totalSeconds % 60;

    minutes = minutes.toString().padStart(2, '0');
    seconds = seconds.toString().padStart(2, '0');

    return `${minutes}:${seconds}`;
});

const formattedPlaybackTime = computed(() => {
    const time = playbackState.value.isPlaying || playbackState.value.currentTime > 0
        ? playbackState.value.currentTime
        : playbackWavesurfer.value?.getDuration() || 0;

    const minutes = Math.floor(time / 60).toString().padStart(2, '0');
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');

    return `${minutes}:${seconds}`;
});

const createWaveSurfer = () => {
    recordingWavesurfer.value = WaveSurfer.create({
        container: '#mic',
        waveColor: '#ec552f',
        height: 40,
        cursorColor: 'transparent',
        cursorWidth: 0,
        barWidth: 3,
        barGap: 3,
        barRadius: 3,
    });

    record.value = recordingWavesurfer.value.registerPlugin(RecordPlugin.create({
        scrollingWaveform: true,
    }));

    record.value.on('record-end', async (blob) => {
        audioBlob.value = await convertWebMToOggOpus(blob)
        hasRecording.value = true;

        if (playbackWavesurfer.value) {
            playbackWavesurfer.value.destroy();
        }
        playbackWavesurfer.value = WaveSurfer.create({
            container: '#playback',
            waveColor: '#6d6d6d',
            progressColor: '#ec552f',
            height: 40,
            cursorColor: 'transparent',
            cursorWidth: 0,
            barWidth: 3,
            barGap: 3,
            barRadius: 3,
        });

        playbackWavesurfer.value.load(URL.createObjectURL(audioBlob.value));
        playbackWavesurfer.value.on('ready', () => {
            playbackState.value.currentTime = playbackWavesurfer.value.getDuration();
        });
        setupPlaybackEventListeners();
    });

    record.value.on('record-progress', throttledRecordProgress);
};

const stopRecording = () => {
    if (!record.value) return;
    record.value.stopRecording();
    recordingState.value = 'Stopped'
};

const cancelRecording = () => {
    store.commit('Chat/setShowRecordSection', false);
}

const togglePlayback = () => {
    if (!playbackWavesurfer.value) return;

    if (playbackWavesurfer.value.isPlaying()) {
        playbackWavesurfer.value.pause();
    } else {
        playbackWavesurfer.value.play();
    }
};

const throttledRecordProgress = throttle((time) => {
    progressTime.value = time;
}, 1000);

const throttledAudioProcess = throttle(() => {
    playbackState.value.currentTime = playbackWavesurfer.value.getCurrentTime();
}, 1000);

const setupPlaybackEventListeners = () => {
    playbackWavesurfer.value.on('play', () => playbackState.value.isPlaying = true);
    playbackWavesurfer.value.on('pause', () => playbackState.value.isPlaying = false);
    playbackWavesurfer.value.on('finish', () => {
        playbackState.value.isPlaying = false;
        playbackState.value.currentTime = playbackWavesurfer.value.getDuration();
    });
    playbackWavesurfer.value.on('audioprocess', throttledAudioProcess);
};

const uploadAudioToFirebase = async () => {
    try {
        uploadAudioLoading.value = true;
        const now = new Date();
        const fileName = `${now.getTime()}.ogg`;
        const path = `tenants/${props.conversation.tenantId}/audios/${fileName}`;
        const uploadRef = storageRef(storage, path);
        // Assuming you want to change the MIME type but keep the blob content unchanged.
        const modifiedAudioBlob = new Blob([audioBlob.value], { type: 'audio/ogg;codecs=opus' });
        const audioDuration = playbackWavesurfer.value.getDuration();
        const customMetadata = {
            contentType: 'audio/ogg',
            duration: `${audioDuration}`, // Store duration as a string or as needed
        };

        const uploadPromise = new Promise((resolve, reject) => {
            const uploadTask = uploadBytesResumable(uploadRef, audioBlob.value);

            uploadTask.on("state_changed",
                (snapshot) => { },
                (error) => reject(error), // On error
                async () => {
                    const audioURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve(audioURL);
                }
            );
        });

        const audioURL = await uploadPromise;
        const messageBody = {
            tenant_id: props.conversation.tenantId,
            customer_phone_number: props.conversation.id,
            customer_profile_name: props.conversation.profile_name,
            type: MessageTypes.Audio,
            content: {
                url: audioURL,
                caption: null,
                voice: true
            },
        }
        if (store.state.Chat.replyToMessage) messageBody.reply_message_id = store.state.Chat.replyToMessage.message_id
        await store.dispatch('Chat/sendMessage', { data: messageBody });
        await store.dispatch("Chat/clearReplyToMessage");
        cancelRecording()
    } catch (error) {
        console.error("Error uploading audio file:", error);
    } finally {
        uploadAudioLoading.value = false;
    }
};

onMounted(() => {
    createWaveSurfer();
    record.value.startRecording();
    recordingState.value = 'Recording'
});

onUnmounted(() => {
    if (recordingWavesurfer.value) recordingWavesurfer.value.destroy();
    if (playbackWavesurfer.value) playbackWavesurfer.value.destroy();
});
</script>

<style scoped lang="scss">
@import './styles/RecordAudio.scss'
</style>
